// ============================================================
// importFields.ts
// Target fields the CSV importer maps columns onto, plus value coercion
// + header auto-mapping. Two catalogs:
//   - CLIENT_IMPORT_FIELDS: keys of CreateClientPayload (createClientApi).
//     Used ONLY in "new cases" mode (each row creates a client).
//   - CASE_IMPORT_FIELDS: whitelisted Case fields (updateCaseApi's
//     UPDATE_CASE_WHITELIST). Used in BOTH modes to set case data after
//     createCaseApi. Every key here is confirmed present in the backend
//     whitelist, and its `type` matches the whitelist `kind`, so a
//     correctly-coerced value never trips the 400 type guard.
//
// `type` drives coercion: a mismatch (e.g. a non-boolean in a boolean
// field) is surfaced as a per-row validation ERROR in preview, not sent
// to the backend — so a bad cell flags its row instead of 400-ing it.
// ============================================================
import { addYearsIso, londonToday, ukDate, yearsBetween } from './civilDate'

export type ImportFieldType = 'string' | 'boolean' | 'number' | 'date' | 'enum' | 'time'

export interface ImportField {
  key: string
  label: string
  type: ImportFieldType
  // 'enum' only: the CANONICAL stored values (what the section's select /
  // radios use). Matching is case- and punctuation-insensitive, and the
  // canonical value is what's sent — so "Hit In The Rear" → "hit in the rear".
  options?: readonly string[]
  // 'number' only: inclusive upper bound (whole numbers ≥ 0).
  max?: number
  // Client identity fields — at least one required per row in new-cases
  // mode (can't create a nameless client).
  identity?: boolean
}

// ── CLIENT fields (new-cases mode) — keys map to CreateClientPayload ──
export const CLIENT_IMPORT_FIELDS: readonly ImportField[] = [
  { key: 'forename', label: 'Client forename', type: 'string', identity: true },
  { key: 'surname', label: 'Client surname', type: 'string', identity: true },
  { key: 'title', label: 'Client title', type: 'enum', options: ['Mr', 'Mrs', 'Miss', 'Ms', 'Dr', 'Other'] },
  { key: 'dateOfBirth', label: 'Client date of birth', type: 'date' },
  { key: 'mainContactNumber', label: 'Client contact number', type: 'string' },
  { key: 'emailAddress', label: 'Client email', type: 'string' },
  // B3b — "Client has email address" (no = no email address; the email is
  // then ignored). The export's clientHasEmailAddress column maps here.
  { key: 'hasEmailAddress', label: 'Client has email address', type: 'boolean' },
  { key: 'addressLine1', label: 'Client address line 1', type: 'string' },
  { key: 'addressLine2', label: 'Client address line 2', type: 'string' },
  { key: 'addressTown', label: 'Client town', type: 'string' },
  { key: 'addressCounty', label: 'Client county', type: 'string' },
  { key: 'addressPostcode', label: 'Client postcode', type: 'string' },
  { key: 'niNumber', label: 'Client NI number', type: 'string' },
  { key: 'occupation', label: 'Client occupation', type: 'string' },
  { key: 'employmentStatus', label: 'Client employment status', type: 'enum', options: ['employed', 'self-employed', 'unemployed'] },
  { key: 'gpName', label: 'GP name', type: 'string' },
  { key: 'gpPhone', label: 'GP phone', type: 'string' },
  { key: 'hospitalName', label: 'Hospital name', type: 'string' },
  { key: 'hospitalPhone', label: 'Hospital phone', type: 'string' },
]

// ── CASE fields (both modes) — keys ∈ UPDATE_CASE_WHITELIST ──
export const CASE_IMPORT_FIELDS: readonly ImportField[] = [
  { key: 'caseName', label: 'Case name', type: 'string' },
  // Case control
  // Handover is a DATE field in the Automatic section (DateInput reads only
  // YYYY-MM-DD) — imported as free text it displayed blank and was nulled on
  // the first blur.
  { key: 'handover', label: 'Handover', type: 'date' },
  { key: 'openDate', label: 'Open date', type: 'date' },
  { key: 'closeDate', label: 'Close date', type: 'date' },
  // Accident
  { key: 'accidentDate', label: 'Accident date', type: 'date' },
  { key: 'accidentTime', label: 'Accident time', type: 'time' },
  { key: 'accidentLocation', label: 'Accident location', type: 'string' },
  { key: 'accidentDescription', label: 'Accident description', type: 'enum', options: ['hit in the rear', 'out of side road', 'changed lanes', 'concertina', 'hit whilst parked', 'hit by oncoming car', 'other'] },
  { key: 'driverName', label: 'Driver name', type: 'string' },
  { key: 'claimantReg', label: 'Claimant vehicle reg', type: 'string' },
  { key: 'vehiclePosition', label: 'Vehicle position', type: 'enum', options: ['driver', 'passenger'] },
  { key: 'numberOfPeopleInVehicle', label: 'People in vehicle', type: 'number', max: 99 },
  { key: 'wearingSeatbelt', label: 'Wearing seatbelt', type: 'boolean' },
  { key: 'policeInvolvement', label: 'Police involvement', type: 'boolean' },
  { key: 'claimantVehicleResponsible', label: 'Claimant vehicle responsible', type: 'boolean' },
  { key: 'askCueReference', label: 'AskCue reference', type: 'string' },
  // Injuries (boolean flags) — every area the export writes, so an exported
  // file imports back (B3b: the B3 areas, "Other" + its details, and the
  // older areas that were missing).
  { key: 'injuryNeck', label: 'Injury: neck', type: 'boolean' },
  { key: 'injuryShoulder', label: 'Injury: shoulder', type: 'boolean' },
  { key: 'injuryBack', label: 'Injury: back', type: 'boolean' },
  { key: 'injuryRibsChestTorso', label: 'Injury: ribs/chest/torso', type: 'boolean' },
  { key: 'injuryFaceCheckboneJawNose', label: 'Injury: face/jaw/nose', type: 'boolean' },
  { key: 'injuryForearmWristHandFingers', label: 'Injury: arm/wrist/hand', type: 'boolean' },
  { key: 'injuryDamageToTeeth', label: 'Injury: teeth', type: 'boolean' },
  { key: 'injuryHeadSenses', label: 'Injury: head/senses', type: 'boolean' },
  { key: 'injuryHipsPelvisGenitals', label: 'Injury: hips/pelvis', type: 'boolean' },
  { key: 'injuryLegKneeAnkleFootToe', label: 'Injury: leg/knee/foot', type: 'boolean' },
  { key: 'injuryPsychological', label: 'Injury: psychological', type: 'boolean' },
  { key: 'injuryHearingEars', label: 'Injury: hearing/ears', type: 'boolean' },
  { key: 'injuryLungsBreathing', label: 'Injury: lungs/breathing', type: 'boolean' },
  { key: 'injurySkin', label: 'Injury: skin', type: 'boolean' },
  { key: 'injuryInternalOrgans', label: 'Injury: internal organs', type: 'boolean' },
  { key: 'injuryFatal', label: 'Injury: fatal', type: 'boolean' },
  { key: 'injuryOther', label: 'Injury: other', type: 'boolean' },
  { key: 'injuryOtherDetails', label: 'Injury: other details', type: 'string' },
  { key: 'injuryExceptionallySevere', label: 'Injury: exceptionally severe', type: 'boolean' },
  { key: 'injuryExceptionallySevereDetails', label: 'Injury: severe details', type: 'string' },
  // Defendant
  { key: 'defendantName', label: 'Defendant name', type: 'string' },
  { key: 'defendantReg', label: 'Defendant vehicle reg', type: 'string' },
  { key: 'defendantMainContactNumber', label: 'Defendant contact number', type: 'string' },
  { key: 'defendantAddressLine1', label: 'Defendant address line 1', type: 'string' },
  { key: 'defendantAddressTown', label: 'Defendant town', type: 'string' },
  { key: 'defendantAddressPostcode', label: 'Defendant postcode', type: 'string' },
  // Medical & third parties
  { key: 'medicalAgencyName', label: 'Medical agency', type: 'string' },
  { key: 'medicalAgencyPhone', label: 'Medical agency phone', type: 'string' },
  { key: 'medicalExpertName', label: 'Medical expert', type: 'string' },
  { key: 'tpsName', label: 'Third-party solicitor', type: 'string' },
  { key: 'tpiInsurer', label: 'TPI insurer', type: 'string' },
  { key: 'tpiReferenceNumber', label: 'TPI reference', type: 'string' },
  // Funding
  { key: 'ateProvider', label: 'ATE provider', type: 'string' },
]

// ── Value coercion ──────────────────────────────────────────
export type CoerceResult =
  | { status: 'empty' }
  | { status: 'ok'; value: string | number | boolean }
  | { status: 'error'; message: string }
  // A date with a 2-digit year and no century chosen yet — the review step
  // ASKS (19xx or 20xx) for the whole column; it never guesses.
  | { status: 'ambiguous'; message: string }
export type Century = 1900 | 2000

const TRUE_SET = new Set(['true', 'yes', 'y', '1', 't'])
const FALSE_SET = new Set(['false', 'no', 'n', '0', 'f'])

// Normalise a date-ish string to ISO YYYY-MM-DD. Accepts already-ISO,
// UK DD/MM/YYYY (and DD-MM-YYYY), or anything Date can parse. Returns
// null when unparseable so the caller can flag the row.
const toIsoDate = (raw: string, century?: Century): string | null | 'ambiguous' => {
  const s = raw.trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    // Real calendar date only (2024-02-31 / 2024-13-45 rejected).
    const [y, m, d] = s.split('-').map(Number)
    const dt = new Date(Date.UTC(y, m - 1, d))
    return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d && y >= 1900 ? s : null
  }
  // UK D/M/YYYY or D/M/YY (also - or . separators). A 2-digit year is NOT
  // guessed: without a chosen century it's 'ambiguous' (the review step asks
  // 19xx or 20xx for the column). Must be a REAL calendar date (31/02 rejected).
  const uk = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/)
  if (uk) {
    const [, d, m, yRaw] = uk
    let y = Number(yRaw)
    if (yRaw.length === 2) {
      if (!century) return 'ambiguous'
      y = century + y
    }
    const dd = Number(d)
    const mm = Number(m)
    const dt = new Date(Date.UTC(y, mm - 1, dd))
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mm - 1 || dt.getUTCDate() !== dd) return null
    return `${String(y).padStart(4, '0')}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  }
  // A purely NUMERIC date that didn't match above (e.g. 2024/03/05, 3-4-24x)
  // is ambiguous — the browser would parse it US-style (M/D). Reject rather
  // than silently swapping day and month.
  if (!/[a-z]/i.test(s)) return null
  // Anything else Date can parse (e.g. "1 June 2023") is parsed in LOCAL
  // time → read back its LOCAL calendar parts. (toISOString().slice(0,10)
  // converted to UTC first, which in BST gave the PREVIOUS day — an accident
  // date / DOB imported a day early.)
  const parsed = new Date(s)
  if (!Number.isNaN(parsed.getTime())) {
    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`
  }
  return null
}

// Coerce a raw CSV cell to the field's type. Empty cells are 'empty'
// (skipped — never sent, so an import never blanks a field it didn't map
// a value for). Bad values are 'error' (flag the row in preview).
const enumKey = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '')

export const coerceValue = (type: ImportFieldType, raw: string, field?: Pick<ImportField, 'options' | 'max'>, opts: { century?: Century } = {}): CoerceResult => {
  const s = (raw ?? '').trim()
  if (s === '') return { status: 'empty' }
  switch (type) {
    case 'string':
      return { status: 'ok', value: s }
    case 'boolean': {
      const low = s.toLowerCase()
      if (TRUE_SET.has(low)) return { status: 'ok', value: true }
      if (FALSE_SET.has(low)) return { status: 'ok', value: false }
      return { status: 'error', message: `"${raw}" is not yes/no` }
    }
    case 'number': {
      // Plain digits only — Number() also accepted "0x10", "1e2", "-3".
      if (!/^\d+$/.test(s)) return { status: 'error', message: `"${raw}" is not a whole number` }
      const n = Number(s)
      const max = field?.max ?? 2147483647
      if (n > max) return { status: 'error', message: `"${raw}" is too large (max ${max})` }
      return { status: 'ok', value: n }
    }
    case 'enum': {
      const opts = field?.options ?? []
      const hit = opts.find((o) => enumKey(o) === enumKey(s))
      if (!hit) return { status: 'error', message: `"${raw}" is not one of: ${opts.join(', ')}` }
      return { status: 'ok', value: hit }
    }
    case 'time': {
      const m = s.match(/^(\d{1,2})[:.](\d{2})(?::\d{2})?$/)
      if (!m || Number(m[1]) > 23 || Number(m[2]) > 59) return { status: 'error', message: `"${raw}" is not a time (HH:MM)` }
      return { status: 'ok', value: `${m[1].padStart(2, '0')}:${m[2]}` }
    }
    case 'date': {
      const iso = toIsoDate(s, opts.century)
      if (iso === 'ambiguous') return { status: 'ambiguous', message: `"${raw}" has a 2-digit year — choose 19xx or 20xx above` }
      if (iso === null) return { status: 'error', message: `"${raw}" is not a valid date` }
      return { status: 'ok', value: iso }
    }
  }
}

// ── Header auto-mapping ─────────────────────────────────────
// Normalise for fuzzy matching: lowercase, strip everything non-alnum.
const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '')

// Suggest a field key for each header by matching (normalised) against
// each field's key and label. Returns header-index → fieldKey ('' = none).
export const autoMapHeaders = (
  headers: string[],
  fields: readonly ImportField[],
): Record<number, string> => {
  const byNorm = new Map<string, string>()
  for (const f of fields) {
    byNorm.set(norm(f.key), f.key)
    byNorm.set(norm(f.label), f.key)
  }
  const out: Record<number, string> = {}
  headers.forEach((h, i) => {
    out[i] = byNorm.get(norm(h)) ?? ''
  })
  return out
}

// ── Date RULES for an imported row (same as editing the field by hand) ──
// Errors skip the row (the server would refuse them too); warnings are shown
// amber on the review step and the row still imports.
//   blocks : accident / DOB / close date in the future; accident before DOB;
//            close before open.
//   warns  : DOB under 18 / over 110 today; accident more than 3 years ago.
// (Follow-ups 29 Oct) "Other" injury ticked with no description → the row
// can't be imported (the server refuses it too, all or nothing).
export const OTHER_INJURY_UNDESCRIBED = 'Describe the ‘Other’ injury (the “Injury: other details” column).'
export const otherInjuryIssue = (c: Record<string, unknown>): string | null =>
  c.injuryOther === true && !(typeof c.injuryOtherDetails === 'string' && c.injuryOtherDetails.trim()) ? OTHER_INJURY_UNDESCRIBED : null
export const importDateRuleIssues = (
  dob: unknown, c: Record<string, unknown>, today: string = londonToday(),
): { errors: string[]; warnings: string[] } => {
  const iso = (v: unknown): string | null => (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null)
  const d = iso(dob); const acc = iso(c.accidentDate); const open = iso(c.openDate); const close = iso(c.closeDate)
  const errors: string[] = []; const warnings: string[] = []
  if (acc && acc > today) errors.push('Accident date can’t be in the future.')
  if (d && d > today) errors.push('Date of birth can’t be in the future.')
  if (acc && d && acc < d) errors.push(`Accident date is before the client’s date of birth (${ukDate(d)}).`)
  if (close && close > today) errors.push('Close date can’t be in the future.')
  if (close && open && close < open) errors.push(`Close date is before the open date (${ukDate(open)}).`)
  if (d && d <= today) {
    const age = yearsBetween(d, today)
    if (age < 18) warnings.push('Under 18 — limitation usually runs from their 18th birthday.')
    if (age > 110) warnings.push('Over 110 — check the date of birth.')
  }
  const threeAgo = addYearsIso(today, -3)
  if (acc && threeAgo && acc < threeAgo && acc <= today) warnings.push('Accident more than 3 years ago — check limitation.')
  return { errors, warnings }
}
